import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { UnixTimestamp } from "../../../models/common";
import { DocumentId, DocumentMetadata } from "../../../models/document";
import { Person } from "../../../models/person";
import { Code, code } from "../../../models/structuration";
import { AllPatientsDropdown } from "../../patients/components/AllPatientsDropdown";
import { JoyDatePicker } from "../../notes/components/Datepickers/JoyDatePicker";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { PatientCard } from "../../../shared/components/PatientCard";
import {
  DocumentQuartzClientContext,
  PersonQuartzClientContext,
} from "../../../shared/services/api/contexts";
import { generateUnixTimestamp, unixToDayjs } from "../../../shared/utils/time";
import { useValuesetCodings } from "../../../shared/hooks/useValuesetCodings";

interface DocumentMetadataDrawerProps {
  documentId: DocumentId;
}

interface MetadataState {
  document: DocumentMetadata | null;
  person: Person | null;
  loading: boolean;
  error: string | null;
  isEditing: boolean;
  saving: boolean;
}

interface FormState {
  filename: string;
  documentDate: Dayjs | null;
  documentType: Code | null;
  documentCategory: Code | null;
  selectedPatient: Person | null;
}

const MetadataItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
}> = ({ icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box sx={{ color: "text.secondary", mt: 0.5 }}>{icon}</Box>
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      <Typography level="body-xs" color="neutral" fontWeight="md">
        {label}
      </Typography>
      <Typography level="body-sm">{value}</Typography>
    </Stack>
  </Stack>
);

const getContentTypeChip = (contentType: string) => {
  if (contentType.includes("pdf")) {
    return (
      <Chip color="danger" size="sm">
        PDF
      </Chip>
    );
  } else if (contentType.includes("text")) {
    return (
      <Chip color="primary" size="sm">
        Text
      </Chip>
    );
  } else if (contentType.includes("word") || contentType.includes("document")) {
    return (
      <Chip color="neutral" size="sm">
        Word Document
      </Chip>
    );
  }
  return (
    <Chip color="neutral" size="sm">
      {contentType}
    </Chip>
  );
};

export const DocumentMetadataDrawer: React.FC<DocumentMetadataDrawerProps> = ({ documentId }) => {
  const { showSnackbar } = useSnackbar();
  const documentQuartzClient = React.useContext(DocumentQuartzClientContext);
  const personQuartzClient = React.useContext(PersonQuartzClientContext);
  const [state, setState] = React.useState<MetadataState>({
    document: null,
    person: null,
    loading: true,
    error: null,
    isEditing: false,
    saving: false,
  });

  const [formState, setFormState] = React.useState<FormState>({
    filename: "",
    documentDate: null,
    documentType: null,
    documentCategory: null,
    selectedPatient: null,
  });

  const {
    codings: documentTypeCodings,
    loading: documentTypeLoading,
    error: documentTypeError,
    getDisplayForCode: getDocumentTypeDisplay,
  } = useValuesetCodings("document-type");

  const {
    codings: documentCategoryCodings,
    loading: documentCategoryLoading,
    error: documentCategoryError,
    getDisplayForCode: getDocumentCategoryDisplay,
  } = useValuesetCodings("medical-specialties");

  const formatDate = (timestamp: UnixTimestamp) => {
    try {
      return unixToDayjs(timestamp)?.format("DD MMM YYYY") || "Unknown date";
    } catch {
      return "Unknown date";
    }
  };

  React.useEffect(() => {
    const loadDocumentMetadata = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const metadata = await documentQuartzClient.getDocumentMetadata(documentId);
        const person = metadata.person_id
          ? await personQuartzClient.getPersonById(metadata.person_id)
          : null;

        setState({
          document: metadata,
          person,
          loading: false,
          error: null,
          isEditing: false,
          saving: false,
        });

        // Initialize form state with loaded data
        setFormState({
          filename: metadata.filename,
          documentDate: metadata.document_date ? unixToDayjs(metadata.document_date) : null,
          documentType: metadata.document_type || null,
          documentCategory: metadata.document_category || null,
          selectedPatient: person,
        });
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message || "Failed to load document metadata",
        }));
        showSnackbar({ text: error.message, color: "danger" });
      }
    };

    if (documentId) {
      loadDocumentMetadata();
    }
  }, [documentId, showSnackbar, documentQuartzClient, personQuartzClient]);

  const handleEditClick = () => {
    setState((prev) => ({ ...prev, isEditing: true }));
  };

  const handleCancelEdit = () => {
    if (!state.document) return;

    // Reset form state to original values
    setFormState({
      filename: state.document.filename,
      documentDate: state.document.document_date ? unixToDayjs(state.document.document_date) : null,
      documentType: state.document.document_type || null,
      documentCategory: state.document.document_category || null,
      selectedPatient: state.person,
    });

    setState((prev) => ({ ...prev, isEditing: false }));
  };

  const handleSaveMetadata = async () => {
    if (!state.document) return;

    setState((prev) => ({ ...prev, saving: true }));

    try {
      const updatedMetadata = {
        filename: formState.filename,
        document_date: formState.documentDate
          ? generateUnixTimestamp(formState.documentDate)
          : null,
        document_type: formState.documentType,
        document_category: formState.documentCategory,
        person_id: formState.selectedPatient?.person_id || null,
        reviewed: true, // Mark as reviewed when saving
      };

      // Call the API to update the document metadata
      const updated = await documentQuartzClient.updateDocumentMetadata(
        state.document.document_id,
        updatedMetadata as any
      );

      // Update the local state
      setState((prev) => ({
        ...prev,
        document: updated,
        person: formState.selectedPatient,
        isEditing: false,
        saving: false,
      }));

      showSnackbar({ text: "Document metadata updated successfully", color: "success" });
    } catch (error: any) {
      setState((prev) => ({ ...prev, saving: false }));
      showSnackbar({ text: error.message || "Failed to update metadata", color: "danger" });
    }
  };

  const hasChanges = () => {
    if (!state.document) return false;

    const originalDate = state.document.document_date
      ? unixToDayjs(state.document.document_date)
      : null;
    return (
      formState.filename !== state.document.filename ||
      !dayjs(formState.documentDate).isSame(originalDate) ||
      formState.documentType !== state.document.document_type ||
      formState.documentCategory !== state.document.document_category ||
      formState.selectedPatient?.person_id !== state.document.person_id
    );
  };

  const isFormValid = () => {
    return (
      formState.filename.trim() !== "" &&
      formState.documentDate !== null &&
      formState.documentType !== null &&
      formState.selectedPatient !== null
      // document_category is optional
    );
  };

  if (state.loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size="sm" />
          <Typography level="body-sm">Loading metadata...</Typography>
        </Stack>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Card color="danger" variant="soft">
          <CardContent>
            <Typography level="body-sm" color="danger">
              {state.error}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!state.document) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="body-sm" color="neutral">
          No document metadata available
        </Typography>
      </Box>
    );
  }

  const { document, person } = state;

  // Render edit mode
  if (state.isEditing) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon color="primary" />
            <Typography level="h4">Edit Document Metadata</Typography>
            {document.reviewed && <CheckCircleIcon color="success" sx={{ ml: "auto" }} />}
          </Box>

          <Divider />

          <Stack spacing={2}>
            <FormControl error={formState.filename.trim() === ""}>
              <FormLabel>Filename *</FormLabel>
              <Input
                value={formState.filename}
                onChange={(e) => setFormState((prev) => ({ ...prev, filename: e.target.value }))}
                placeholder="Enter filename"
                color={formState.filename.trim() === "" ? "danger" : "neutral"}
              />
              {formState.filename.trim() === "" && (
                <Typography level="body-xs" color="danger">
                  Filename is required
                </Typography>
              )}
            </FormControl>

            <FormControl error={formState.documentDate === null}>
              <JoyDatePicker
                label="Document Date *"
                value={formState.documentDate}
                onChange={(newValue: Dayjs | null) =>
                  setFormState((prev) => ({ ...prev, documentDate: newValue }))
                }
                slotProps={{
                  textField: {
                    color: formState.documentDate === null ? "warning" : "primary",
                  },
                }}
              />
              {formState.documentDate === null && (
                <Typography level="body-xs" color="danger">
                  Document date is required
                </Typography>
              )}
            </FormControl>

            <FormControl error={formState.documentType === null}>
              <FormLabel>Document Type *</FormLabel>
              <Select
                value={formState.documentType}
                onChange={(_, newValue) =>
                  setFormState((prev) => ({ ...prev, documentType: newValue }))
                }
                placeholder={documentTypeLoading ? "Loading types..." : "Select document type"}
                color={formState.documentType === null ? "danger" : "neutral"}
                disabled={documentTypeLoading}
              >
                {documentTypeCodings.map((type) => (
                  <Option key={type.code} value={code(type.code)}>
                    {type.display || type.code}
                  </Option>
                ))}
              </Select>
              {formState.documentType === null && (
                <Typography level="body-xs" color="danger">
                  Document type is required
                </Typography>
              )}
              {documentTypeError && (
                <Typography level="body-xs" color="danger">
                  {documentTypeError}
                </Typography>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Document Category</FormLabel>
              <Select
                value={formState.documentCategory}
                onChange={(_, newValue) =>
                  setFormState((prev) => ({ ...prev, documentCategory: newValue }))
                }
                placeholder={
                  documentCategoryLoading
                    ? "Loading categories..."
                    : "Select document category (optional)"
                }
                disabled={documentCategoryLoading}
              >
                {documentCategoryCodings.map((category) => (
                  <Option key={category.code} value={code(category.code)}>
                    {category.display || category.code}
                  </Option>
                ))}
              </Select>
              {documentCategoryError && (
                <Typography level="body-xs" color="danger">
                  {documentCategoryError}
                </Typography>
              )}
            </FormControl>

            <Box>
              <FormControl error={formState.selectedPatient === null}>
                <AllPatientsDropdown
                  formLabel="Patient *"
                  onSelect={(person) =>
                    setFormState((prev) => ({ ...prev, selectedPatient: person }))
                  }
                />
                {formState.selectedPatient === null && (
                  <Typography level="body-xs" color="danger">
                    Patient selection is required
                  </Typography>
                )}
              </FormControl>
              {formState.selectedPatient && (
                <PatientCard
                  patient={{
                    person_id: formState.selectedPatient.person_id,
                    given_name: formState.selectedPatient.given_name || undefined,
                    family_name: formState.selectedPatient.family_name || undefined,
                    gender: formState.selectedPatient.gender || undefined,
                  }}
                />
              )}
            </Box>
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleCancelEdit} disabled={state.saving}>
              Cancel
            </Button>
            <Button
              startDecorator={<SaveIcon />}
              onClick={handleSaveMetadata}
              disabled={!hasChanges() || state.saving || !isFormValid()}
              loading={state.saving}
            >
              Validate Metadata
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Render view mode
  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography level="h4">Document Information</Typography>
          <Button
            startDecorator={<EditIcon />}
            onClick={handleEditClick}
            variant="outlined"
            color="primary"
            size="sm"
          >
            Edit Metadata
          </Button>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <MetadataItem
                icon={<DescriptionIcon fontSize="small" />}
                label="Filename"
                value={document.filename}
              />

              <MetadataItem
                icon={<FolderIcon fontSize="small" />}
                label="Content Type"
                value={getContentTypeChip(document.content_type)}
              />

              {document.document_date && (
                <MetadataItem
                  icon={<CalendarTodayIcon fontSize="small" />}
                  label="Document Date"
                  value={formatDate(document.document_date)}
                />
              )}

              {document.document_type && (
                <MetadataItem
                  icon={<DescriptionIcon fontSize="small" />}
                  label="Document Type"
                  value={getDocumentTypeDisplay(document.document_type || undefined)}
                />
              )}

              {document.document_category && (
                <MetadataItem
                  icon={<FolderIcon fontSize="small" />}
                  label="Document Category"
                  value={getDocumentCategoryDisplay(document.document_category || undefined)}
                />
              )}

              <MetadataItem
                icon={<CalendarTodayIcon fontSize="small" />}
                label="Upload Date"
                value={formatDate(document.upload_date)}
              />

              <MetadataItem
                icon={<DescriptionIcon fontSize="small" />}
                label="Review Status"
                value={
                  document.reviewed ? (
                    <Chip size="sm" color="success">
                      Reviewed
                    </Chip>
                  ) : (
                    <Chip size="sm" color="warning">
                      Needs Review
                    </Chip>
                  )
                }
              />
            </Stack>
          </CardContent>
        </Card>

        {person && (
          <>
            <Divider />
            <Typography level="title-md">Patient Information</Typography>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <MetadataItem
                    icon={<PersonIcon fontSize="small" />}
                    label="Patient Name"
                    value={
                      `${person.given_name || ""} ${person.family_name || ""}`.trim() || "Unknown"
                    }
                  />

                  {person.birth_date && (
                    <MetadataItem
                      icon={<CalendarTodayIcon fontSize="small" />}
                      label="Date of Birth"
                      value={formatDate(person.birth_date)}
                    />
                  )}

                  {person.gender && (
                    <MetadataItem
                      icon={<PersonIcon fontSize="small" />}
                      label="Gender"
                      value={person.gender}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </>
        )}

        {document.person_id && !person && (
          <>
            <Divider />
            <Typography level="title-md">Patient Information</Typography>
            <Card variant="soft" color="warning">
              <CardContent>
                <Typography level="body-sm">
                  Patient information is not available for this document.
                </Typography>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Box>
  );
};
